/**
 * layout - the task page's binding of the shared detail-page surface.
 *
 * A task has three panes over its canvas:
 *
 *   context   status, priority, dates, who made it        (left, open)
 *   access    the collaborator list and the way in        (right)
 *   activity  the audit log                               (right)
 *
 * NO READER PANE. Attachments open in the app's global side panel, the way a
 * document opens everywhere else - see `TaskDetail.handleOpenAttachment`. A
 * removed id simply stops being read out of a stored layout (`validateLayout`
 * walks `surface.ids`), so nobody's saved arrangement breaks on the way.
 *
 * The centre is the CANVAS - the tab bar and whichever tab is open (Details,
 * Feed, Attachments) - always present, never tiled, with the panes floating
 * over it.
 *
 * WHAT THESE REPLACE. The audit log was a hand-rolled `fixed inset-y-0
 * end-0 w-96 glass` drawer with its own `bg-black/20` scrim, living outside
 * every layout the page had and reachable only from a menu item that toggled
 * a `showAuditLog` boolean. The collaborator list was three avatar bubbles in
 * the header and a `manageResourceCollaborators` side panel over the whole
 * page. Both are panes now: they dock, stack, reorder, resize and persist
 * like every other pane in the app.
 *
 * WHY GLOBAL SCOPE - and this is the one place tasks differ from projects and
 * matters. A matter is a place someone works for weeks and arranges to suit
 * that matter; a task is one of four hundred rows, opened for a minute. An
 * entity-scoped key would write a layout record per task uuid, evict the
 * oldest at 50, and still never give anyone the arrangement they set two
 * tasks ago. Nobody rearranges four hundred task pages - they arrange THE
 * task page, once. So one key for all of them.
 *
 * NO LEGACY KEYS. This surface is new; nothing was ever written for it.
 *
 * NO DETAIL PANE. The task page edits its record inline on the Details tab
 * and selects nothing, so it binds the selection-free hook - see `./index`.
 */

import {
  PanelBounds,
  PanelLayout as GenericLayout,
  PanelSlot as GenericSlot,
} from '../../shared/surface/panelLayout';
import defineDetailSurface, {
  DetailPaneConfig,
} from '../../shared/surface/defineDetailSurface';

export type TaskPanelId = 'context' | 'access' | 'activity';

/** The canvas's tabs. Labels are the page's - see `TaskDetail`. */
export type TaskTab = 'Details' | 'Feed' | 'Attachments';

export const TASK_TABS: TaskTab[] = ['Details', 'Feed', 'Attachments'];

export type TaskPanelSlot = GenericSlot<TaskPanelId>;
export type TaskPanelLayout = GenericLayout<TaskPanelId>;

export const TASK_PANEL_IDS: TaskPanelId[] = ['context', 'access', 'activity'];

/**
 * Every pane: its label, its resize limits, where it starts, whether it opens
 * on a first visit and whether it gets a rail toggle.
 *
 * ONE IS OPEN BY DEFAULT - context - because those facts were on screen
 * before as the Details tab's metadata grid, and they are what the Feed and
 * Attachments tabs never had. Opening on defaults nobody chose is how a
 * returning user loses their bearings, so the pane set starts where the fixed
 * layout ended.
 */
const TASK_PANES: Record<TaskPanelId, DetailPaneConfig> = {
  // The facts of the task, read against whatever tab is open - which is the
  // whole reason they moved off the Details tab.
  context: {
    label: 'Context',
    bounds: { min: 260, max: 720 },
    dock: 'left', index: 0, width: 320, visible: true, rail: true,
  },
  access: {
    label: 'Access',
    bounds: { min: 240, max: 640 },
    dock: 'right', index: 0, width: 300, visible: false, rail: true,
  },
  activity: {
    label: 'Activity',
    bounds: { min: 280, max: 760 },
    dock: 'right', index: 1, width: 340, visible: false, rail: true,
  },
};

export const TASK_SURFACE = defineDetailSurface<TaskPanelId, TaskTab>({
  kind: 'detail',
  // Also the `data-testid` prefix for the shell: `task-surface`, etc.
  name: 'task',
  scope: 'global',
  ids: TASK_PANEL_IDS,
  panes: TASK_PANES,
  tabs: TASK_TABS,
});

/* ── derived, for call sites and tests that want the plain records ── */

export const TASK_PANEL_LABELS: Record<TaskPanelId, string> = TASK_SURFACE.labels;
export const TASK_PANEL_BOUNDS: Record<TaskPanelId, PanelBounds> = TASK_SURFACE.bounds;
export const TASK_DEFAULT_LAYOUT: TaskPanelLayout = TASK_SURFACE.defaults;
